import { TextWriter, ZipReader, HttpReader } from "@zip.js/zip.js"
import { createCodeViewer } from "./codeviewer.js"
import { createFileTree } from "./filetree.js"
import { createVersionList } from "./versionlist.js"

// https://esbuild.github.io/api/#live-reload
if (process.env.NODE_ENV === "development") {
    new EventSource("/esbuild").addEventListener("change", () => { location.reload() })
}

// Build a breadcrumb element for a module path like filippo.io/age@v1.3.1/internal/age/parse.go.
// Slashes after the @ get spaced out and each segment is a clickable link.
function buildBreadcrumb(modulePath: string, version: string, segments: string[]): HTMLElement {
    const nav = document.createElement("span")

    // Module@version prefix (no spaced slashes, links to root listing).
    const rootLink = document.createElement(segments.length > 0 ? "a" : "span")
    if (rootLink instanceof HTMLAnchorElement) {
        rootLink.href = `/${modulePath}@${version}/`
    }
    rootLink.textContent = `${modulePath}@${version}`
    nav.appendChild(rootLink)

    // Path segments after @version, with spaced / separators.
    let href = `/${modulePath}@${version}/`
    for (let i = 0; i < segments.length; i++) {
        const seg = segments[i]
        const isLast = i === segments.length - 1

        const sep = document.createTextNode(" / ")
        nav.appendChild(sep)

        href += seg
        // Non-final segments are directories and get a trailing slash.
        if (!isLast) {
            href += "/"
        }

        const link = document.createElement(isLast ? "span" : "a")
        if (link instanceof HTMLAnchorElement) {
            link.href = href
        }
        link.textContent = seg
        nav.appendChild(link)
    }

    return nav
}

function showError(message: string): never {
    const box = document.createElement("div")
    box.textContent = message
    Object.assign(box.style, {
        position: "fixed",
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
        padding: "24px 32px",
        background: "#fee2e2",
        color: "#991b1b",
        border: "1px solid #fca5a5",
        borderRadius: "8px",
        fontFamily: "system-ui, sans-serif",
        fontSize: "16px",
        maxWidth: "80vw",
        textAlign: "center",
    })
    document.body.appendChild(box)
    throw new Error(message)
}

async function loadModule(modulePath: string, version: string, filePath: string) {
    // The zip entry prefix is "module@version/".
    const zipEntryPrefix = `${modulePath}@${version}/`

    const zipPath = `https://proxy.golang.org/${modulePath}/@v/${version}.zip`

    // Check that the module exists before trying to read the zip.
    const headResp = await fetch(zipPath, { method: "HEAD" })
    if (!headResp.ok) {
        showError(`Module not found: ${modulePath}@${version}`)
    }

    const zipReader = new ZipReader(new HttpReader(zipPath, { forceRangeRequests: true }))
    const entries = await zipReader.getEntries()

    // Collect all file entries (not directories) with paths relative to the zip prefix.
    const allFiles = entries
        .filter((e) => !e.directory)
        .map((e) => e.filename)

    // Determine if filePath is an exact file or a directory prefix.
    const zipEntryPath = zipEntryPrefix + filePath
    const exactFile = filePath
        ? entries.find((e) => !e.directory && e.filename === zipEntryPath)
        : undefined

    if (exactFile && !exactFile.directory) {
        const text = await exactFile.getData(new TextWriter())
        const header = buildBreadcrumb(modulePath, version, filePath.split("/"))
        const fileName = filePath.split("/").pop() ?? filePath
        const dirPath2 = filePath.includes("/") ? filePath.slice(0, filePath.lastIndexOf("/")) : ""
        const pathPart = `${modulePath}@${version}` + (dirPath2 ? `/${dirPath2}` : "")
        document.title = `${fileName} — ${pathPart}`
        const viewer = createCodeViewer({ code: text, filename: filePath, header })
        document.body.appendChild(viewer)
    } else {
        // Show file listing for this directory. Ensure the prefix ends with /
        // so we don't match e.g. "tag.go" when looking for "tag/".
        const dirPath = filePath && !filePath.endsWith("/") ? filePath + "/" : filePath
        const dirPrefix = zipEntryPrefix + dirPath
        const dirFiles = allFiles.filter((f) => f.startsWith(dirPrefix))
        if (dirFiles.length === 0) {
            showError(`Not found: ${filePath || modulePath}`)
        }
        const baseUrl = `/${modulePath}@${version}/`
        const dirSegments = dirPath ? dirPath.replace(/\/$/, "").split("/") : []
        const header = buildBreadcrumb(modulePath, version, dirSegments)
        document.title = `${modulePath}@${version}` + (dirPath ? `/${dirPath}` : "/")
        const tree = createFileTree({
            filePaths: dirFiles,
            stripPrefix: dirPrefix,
            baseUrl: baseUrl + dirPath,
            header,
        })
        document.body.appendChild(tree)
    }
}

async function loadVersionList(modulePath: string) {
    const listUrl = `https://proxy.golang.org/${modulePath}/@v/list`
    const resp = await fetch(listUrl)
    if (!resp.ok) {
        showError(`Module not found: ${modulePath}`)
    }
    const text = await resp.text()
    const versions = text.trim().split("\n").filter((v) => v.length > 0)
    if (versions.length === 0) {
        showError(`No versions found for ${modulePath}`)
    }
    document.title = modulePath
    const container = createVersionList({ modulePath, versions })
    document.body.appendChild(container)
}

// Parse URL: /filippo.io/age@v1.3.1/age.go
// → module = "filippo.io/age", version = "v1.3.1", file = "age.go"
// Or just /filippo.io/age → show version listing.
const path = window.location.pathname.slice(1) // strip leading /

// Stdlib packages (no dot in first path segment) are not on the module proxy.
// Redirect to cs.opensource.google which hosts the Go source tree.
const firstSegment = path.split("/")[0].split("@")[0]
if (firstSegment && !firstSegment.includes(".")) {
    const atIndex = path.indexOf("@")
    let ref: string
    let pkgPath: string
    let filePart: string
    if (atIndex === -1) {
        ref = "refs/heads/master"
        pkgPath = path.replace(/\/$/, "")
        filePart = ""
    } else {
        pkgPath = path.slice(0, atIndex)
        const rest = path.slice(atIndex + 1)
        const slashIndex = rest.indexOf("/")
        ref = slashIndex === -1 ? rest : rest.slice(0, slashIndex)
        filePart = slashIndex === -1 ? "" : rest.slice(slashIndex + 1)
    }
    const treePath = "src/" + pkgPath + "/" + filePart
    let csUrl = `https://cs.opensource.google/go/go/+/${ref}:${treePath}`
    const hash = window.location.hash
    const lineMatch = /^#L(\d+)/.exec(hash)
    if (lineMatch) {
        csUrl += `;l=${lineMatch[1]}`
    }
    window.location.replace(csUrl)
} else {
    const atIndex = path.indexOf("@")

    if (atIndex === -1) {
        const modulePath = path.replace(/\/$/, "")
        if (!modulePath) {
            showError("Invalid URL: missing module path")
        }
        await loadVersionList(modulePath)
    } else {
        const modulePath = path.slice(0, atIndex)
        const rest = path.slice(atIndex + 1) // e.g. "v1.3.1/age.go" or "v1.3.1/"
        const slashIndex = rest.indexOf("/")
        const version = slashIndex === -1 ? rest : rest.slice(0, slashIndex)
        const filePath = slashIndex === -1 ? "" : rest.slice(slashIndex + 1)
        await loadModule(modulePath, version, filePath)
    }
}
